/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
/**
 * Enhanced TipTap extensions to preserve formatting during paste operations.
 * Extends default extensions to capture inline styles from Word, PDF, Google Docs, etc.
 */

import { Node } from '@tiptap/core';
import { Paragraph } from '@tiptap/extension-paragraph';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { mergeAttributes } from '@tiptap/core';

import { cellStyleHtmlAttributes, normalizeHexColor } from './documentColors';
import { fontFamilyFromStyle } from './documentContentStyles';

function keepFontFamily(style: string, styleObj: Record<string, string>) {
    const fontFamily = fontFamilyFromStyle(style);
    if (fontFamily) {
        styleObj['font-family'] = fontFamily;
    }
}

function keepCellTextColor(computedStyle: CSSStyleDeclaration, styleObj: Record<string, string>) {
    if (
        computedStyle.color &&
        computedStyle.color !== 'inherit' &&
        computedStyle.color !== 'transparent'
    ) {
        styleObj.color = computedStyle.color;
    }
}

function keepCellBgColorAttribute(element: HTMLElement, styleObj: Record<string, string>) {
    if (styleObj['background-color']) return;
    const normalized = normalizeHexColor(element.getAttribute('bgcolor'));
    if (normalized) styleObj['background-color'] = normalized;
}

/**
 * Enhanced Paragraph extension that preserves:
 * - Line height
 * - Text indentation
 * - Margins (top, bottom)
 * - Padding
 * - Text alignment (already handled by TextAlign extension, but we preserve it here too)
 */
export const EnhancedParagraph = Paragraph.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (element) => {
                    const style = element.getAttribute('style');
                    if (!style) return null;

                    // Extract specific style properties we want to preserve
                    const styleObj: Record<string, string> = {};
                    const tempDiv = document.createElement('div');
                    tempDiv.setAttribute('style', style);
                    const computedStyle = tempDiv.style;

                    // Preserve line-height
                    if (computedStyle.lineHeight && computedStyle.lineHeight !== 'normal') {
                        styleObj['line-height'] = computedStyle.lineHeight;
                    }

                    // Preserve text-indent
                    if (computedStyle.textIndent && computedStyle.textIndent !== '0px') {
                        styleObj['text-indent'] = computedStyle.textIndent;
                    }

                    // Preserve margins
                    if (computedStyle.marginTop && computedStyle.marginTop !== '0px') {
                        styleObj['margin-top'] = computedStyle.marginTop;
                    }
                    if (computedStyle.marginBottom && computedStyle.marginBottom !== '0px') {
                        styleObj['margin-bottom'] = computedStyle.marginBottom;
                    }
                    if (computedStyle.marginLeft && computedStyle.marginLeft !== '0px') {
                        styleObj['margin-left'] = computedStyle.marginLeft;
                    }
                    if (computedStyle.marginRight && computedStyle.marginRight !== '0px') {
                        styleObj['margin-right'] = computedStyle.marginRight;
                    }

                    // Preserve padding
                    if (computedStyle.paddingTop && computedStyle.paddingTop !== '0px') {
                        styleObj['padding-top'] = computedStyle.paddingTop;
                    }
                    if (computedStyle.paddingBottom && computedStyle.paddingBottom !== '0px') {
                        styleObj['padding-bottom'] = computedStyle.paddingBottom;
                    }
                    if (computedStyle.paddingLeft && computedStyle.paddingLeft !== '0px') {
                        styleObj['padding-left'] = computedStyle.paddingLeft;
                    }
                    if (computedStyle.paddingRight && computedStyle.paddingRight !== '0px') {
                        styleObj['padding-right'] = computedStyle.paddingRight;
                    }

                    // Preserve text-align as fallback
                    if (computedStyle.textAlign && computedStyle.textAlign !== 'start') {
                        styleObj['text-align'] = computedStyle.textAlign;
                    }

                    keepFontFamily(style, styleObj);

                    // Convert back to string
                    if (Object.keys(styleObj).length > 0) {
                        return Object.entries(styleObj)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('; ');
                    }

                    return null;
                },
                renderHTML: (attributes) => {
                    if (!attributes.style) {
                        return {};
                    }
                    return {
                        style: attributes.style,
                    };
                },
            },
        };
    },
});

/**
 * Enhanced Heading extension that preserves line-height and margins
 */
export const createEnhancedHeading = (baseHeading: any) => {
    return baseHeading.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                style: {
                    default: null,
                    parseHTML: (element: HTMLElement) => {
                        const style = element.getAttribute('style');
                        if (!style) return null;

                        const styleObj: Record<string, string> = {};
                        const tempDiv = document.createElement('div');
                        tempDiv.setAttribute('style', style);
                        const computedStyle = tempDiv.style;

                        // Preserve line-height for headings
                        if (computedStyle.lineHeight && computedStyle.lineHeight !== 'normal') {
                            styleObj['line-height'] = computedStyle.lineHeight;
                        }

                        // Preserve margins for headings
                        if (computedStyle.marginTop && computedStyle.marginTop !== '0px') {
                            styleObj['margin-top'] = computedStyle.marginTop;
                        }
                        if (computedStyle.marginBottom && computedStyle.marginBottom !== '0px') {
                            styleObj['margin-bottom'] = computedStyle.marginBottom;
                        }

                        keepFontFamily(style, styleObj);

                        if (Object.keys(styleObj).length > 0) {
                            return Object.entries(styleObj)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join('; ');
                        }

                        return null;
                    },
                    renderHTML: (attributes: Record<string, string>) => {
                        if (!attributes.style) {
                            return {};
                        }
                        return {
                            style: attributes.style,
                        };
                    },
                },
            };
        },
    });
};

/**
 * Enhanced TableCell extension that preserves:
 * - Border styles
 * - Padding
 * - Background color
 * - Vertical alignment
 * - Width
 */
export const EnhancedTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            colspan: {
                default: 1,
                parseHTML: (element) => {
                    const colspan = element.getAttribute('colspan');
                    return colspan ? parseInt(colspan, 10) : 1;
                },
            },
            rowspan: {
                default: 1,
                parseHTML: (element) => {
                    const rowspan = element.getAttribute('rowspan');
                    return rowspan ? parseInt(rowspan, 10) : 1;
                },
            },
            style: {
                default: null,
                parseHTML: (element) => {
                    const style = element.getAttribute('style');
                    if (!style) return null;

                    const styleObj: Record<string, string> = {};
                    const tempDiv = document.createElement('div');
                    tempDiv.setAttribute('style', style);
                    const computedStyle = tempDiv.style;

                    // Preserve borders
                    if (computedStyle.border) {
                        styleObj['border'] = computedStyle.border;
                    }
                    if (computedStyle.borderTop && computedStyle.borderTop !== 'none') {
                        styleObj['border-top'] = computedStyle.borderTop;
                    }
                    if (computedStyle.borderRight && computedStyle.borderRight !== 'none') {
                        styleObj['border-right'] = computedStyle.borderRight;
                    }
                    if (computedStyle.borderBottom && computedStyle.borderBottom !== 'none') {
                        styleObj['border-bottom'] = computedStyle.borderBottom;
                    }
                    if (computedStyle.borderLeft && computedStyle.borderLeft !== 'none') {
                        styleObj['border-left'] = computedStyle.borderLeft;
                    }

                    // Preserve padding
                    if (computedStyle.padding && computedStyle.padding !== '0px') {
                        styleObj['padding'] = computedStyle.padding;
                    } else {
                        if (computedStyle.paddingTop && computedStyle.paddingTop !== '0px') {
                            styleObj['padding-top'] = computedStyle.paddingTop;
                        }
                        if (computedStyle.paddingRight && computedStyle.paddingRight !== '0px') {
                            styleObj['padding-right'] = computedStyle.paddingRight;
                        }
                        if (computedStyle.paddingBottom && computedStyle.paddingBottom !== '0px') {
                            styleObj['padding-bottom'] = computedStyle.paddingBottom;
                        }
                        if (computedStyle.paddingLeft && computedStyle.paddingLeft !== '0px') {
                            styleObj['padding-left'] = computedStyle.paddingLeft;
                        }
                    }

                    // Preserve background color
                    if (
                        computedStyle.backgroundColor &&
                        computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)' &&
                        computedStyle.backgroundColor !== 'transparent'
                    ) {
                        styleObj['background-color'] = computedStyle.backgroundColor;
                    }

                    // Preserve vertical alignment
                    if (computedStyle.verticalAlign && computedStyle.verticalAlign !== 'baseline') {
                        styleObj['vertical-align'] = computedStyle.verticalAlign;
                    }

                    // Preserve width
                    if (computedStyle.width && computedStyle.width !== 'auto') {
                        styleObj['width'] = computedStyle.width;
                    }

                    // Preserve text-align
                    if (computedStyle.textAlign && computedStyle.textAlign !== 'start') {
                        styleObj['text-align'] = computedStyle.textAlign;
                    }

                    keepCellTextColor(computedStyle, styleObj);
                    keepCellBgColorAttribute(element, styleObj);
                    keepFontFamily(style, styleObj);

                    if (Object.keys(styleObj).length > 0) {
                        return Object.entries(styleObj)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('; ');
                    }

                    return null;
                },
                renderHTML: (attributes) => cellStyleHtmlAttributes(attributes.style),
            },
        };
    },

    renderHTML({ HTMLAttributes }) {
        return ['td', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});

/**
 * Enhanced TableHeader extension - similar to TableCell but for <th> elements
 */
export const EnhancedTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            colspan: {
                default: 1,
                parseHTML: (element) => {
                    const colspan = element.getAttribute('colspan');
                    return colspan ? parseInt(colspan, 10) : 1;
                },
            },
            rowspan: {
                default: 1,
                parseHTML: (element) => {
                    const rowspan = element.getAttribute('rowspan');
                    return rowspan ? parseInt(rowspan, 10) : 1;
                },
            },
            style: {
                default: null,
                parseHTML: (element) => {
                    const style = element.getAttribute('style');
                    if (!style) return null;

                    const styleObj: Record<string, string> = {};
                    const tempDiv = document.createElement('div');
                    tempDiv.setAttribute('style', style);
                    const computedStyle = tempDiv.style;

                    // Same as TableCell - preserve borders, padding, background, etc.
                    if (computedStyle.border) {
                        styleObj['border'] = computedStyle.border;
                    }
                    if (computedStyle.borderTop && computedStyle.borderTop !== 'none') {
                        styleObj['border-top'] = computedStyle.borderTop;
                    }
                    if (computedStyle.borderRight && computedStyle.borderRight !== 'none') {
                        styleObj['border-right'] = computedStyle.borderRight;
                    }
                    if (computedStyle.borderBottom && computedStyle.borderBottom !== 'none') {
                        styleObj['border-bottom'] = computedStyle.borderBottom;
                    }
                    if (computedStyle.borderLeft && computedStyle.borderLeft !== 'none') {
                        styleObj['border-left'] = computedStyle.borderLeft;
                    }

                    if (computedStyle.padding && computedStyle.padding !== '0px') {
                        styleObj['padding'] = computedStyle.padding;
                    } else {
                        if (computedStyle.paddingTop && computedStyle.paddingTop !== '0px') {
                            styleObj['padding-top'] = computedStyle.paddingTop;
                        }
                        if (computedStyle.paddingRight && computedStyle.paddingRight !== '0px') {
                            styleObj['padding-right'] = computedStyle.paddingRight;
                        }
                        if (computedStyle.paddingBottom && computedStyle.paddingBottom !== '0px') {
                            styleObj['padding-bottom'] = computedStyle.paddingBottom;
                        }
                        if (computedStyle.paddingLeft && computedStyle.paddingLeft !== '0px') {
                            styleObj['padding-left'] = computedStyle.paddingLeft;
                        }
                    }

                    if (
                        computedStyle.backgroundColor &&
                        computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)' &&
                        computedStyle.backgroundColor !== 'transparent'
                    ) {
                        styleObj['background-color'] = computedStyle.backgroundColor;
                    }

                    if (computedStyle.verticalAlign && computedStyle.verticalAlign !== 'baseline') {
                        styleObj['vertical-align'] = computedStyle.verticalAlign;
                    }

                    if (computedStyle.width && computedStyle.width !== 'auto') {
                        styleObj['width'] = computedStyle.width;
                    }

                    if (computedStyle.textAlign && computedStyle.textAlign !== 'start') {
                        styleObj['text-align'] = computedStyle.textAlign;
                    }

                    // Font weight for headers
                    if (
                        computedStyle.fontWeight &&
                        computedStyle.fontWeight !== 'normal' &&
                        computedStyle.fontWeight !== '400'
                    ) {
                        styleObj['font-weight'] = computedStyle.fontWeight;
                    }

                    keepCellTextColor(computedStyle, styleObj);
                    keepCellBgColorAttribute(element, styleObj);
                    keepFontFamily(style, styleObj);

                    if (Object.keys(styleObj).length > 0) {
                        return Object.entries(styleObj)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('; ');
                    }

                    return null;
                },
                renderHTML: (attributes) => cellStyleHtmlAttributes(attributes.style),
            },
        };
    },

    renderHTML({ HTMLAttributes }) {
        return ['th', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});

/**
 * Enhanced Table extension that preserves table-level styling
 */
export const createEnhancedTable = (baseTable: Node) => {
    return baseTable.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                style: {
                    default: null,
                    parseHTML: (element: HTMLElement) => {
                        const style = element.getAttribute('style');
                        if (!style) return null;

                        const styleObj: Record<string, string> = {};
                        const tempDiv = document.createElement('div');
                        tempDiv.setAttribute('style', style);
                        const computedStyle = tempDiv.style;

                        // Preserve table borders
                        if (computedStyle.border) {
                            styleObj['border'] = computedStyle.border;
                        }
                        if (computedStyle.borderCollapse) {
                            styleObj['border-collapse'] = computedStyle.borderCollapse;
                        }

                        // Preserve table width
                        if (computedStyle.width && computedStyle.width !== 'auto') {
                            styleObj['width'] = computedStyle.width;
                        }

                        // Preserve margins
                        if (computedStyle.marginTop && computedStyle.marginTop !== '0px') {
                            styleObj['margin-top'] = computedStyle.marginTop;
                        }
                        if (computedStyle.marginBottom && computedStyle.marginBottom !== '0px') {
                            styleObj['margin-bottom'] = computedStyle.marginBottom;
                        }

                        if (Object.keys(styleObj).length > 0) {
                            return Object.entries(styleObj)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join('; ');
                        }

                        return null;
                    },
                    renderHTML: (attributes: Record<string, string>) => {
                        if (!attributes.style) {
                            return {};
                        }
                        return {
                            style: attributes.style,
                        };
                    },
                },
            };
        },
    });
};

/**
 * Enhanced ListItem extension to preserve indentation and spacing
 */
export const createEnhancedListItem = (baseListItem: any) => {
    return baseListItem.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                style: {
                    default: null,
                    parseHTML: (element: HTMLElement) => {
                        const style = element.getAttribute('style');
                        if (!style) return null;

                        const styleObj: Record<string, string> = {};
                        const tempDiv = document.createElement('div');
                        tempDiv.setAttribute('style', style);
                        const computedStyle = tempDiv.style;

                        // Preserve margins for list items
                        if (computedStyle.marginTop && computedStyle.marginTop !== '0px') {
                            styleObj['margin-top'] = computedStyle.marginTop;
                        }
                        if (computedStyle.marginBottom && computedStyle.marginBottom !== '0px') {
                            styleObj['margin-bottom'] = computedStyle.marginBottom;
                        }
                        if (computedStyle.marginLeft && computedStyle.marginLeft !== '0px') {
                            styleObj['margin-left'] = computedStyle.marginLeft;
                        }

                        // Preserve padding for list items
                        if (computedStyle.paddingLeft && computedStyle.paddingLeft !== '0px') {
                            styleObj['padding-left'] = computedStyle.paddingLeft;
                        }

                        if (Object.keys(styleObj).length > 0) {
                            return Object.entries(styleObj)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join('; ');
                        }

                        return null;
                    },
                    renderHTML: (attributes: any) => {
                        if (!attributes.style) {
                            return {};
                        }
                        return {
                            style: attributes.style,
                        };
                    },
                },
            };
        },
    });
};

/**
 * Line Height extension - adds line-height control as a mark
 * This provides additional line-height support beyond what paragraph styles offer
 */
export const LineHeight = Node.create({
    name: 'lineHeight',

    addOptions() {
        return {
            types: ['paragraph', 'heading'],
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    lineHeight: {
                        default: null,
                        parseHTML: (element) => {
                            const lineHeight = element.style.lineHeight;
                            return lineHeight || null;
                        },
                        renderHTML: (attributes) => {
                            if (!attributes.lineHeight) {
                                return {};
                            }
                            return {
                                style: `line-height: ${attributes.lineHeight}`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setLineHeight:
                (lineHeight: string) =>
                ({ commands }) => {
                    return this.options.types.every((type: string) =>
                        commands.updateAttributes(type, { lineHeight })
                    );
                },
            unsetLineHeight:
                () =>
                ({ commands }) => {
                    return this.options.types.every((type: string) =>
                        commands.resetAttributes(type, 'lineHeight')
                    );
                },
        };
    },
});
